import type { AppDef } from '../../types/window';
import { dispatch, getState, subscribe } from '../../state/store';
import { allColors, allWallpapers, applyDesktopBackground, FITS } from '../wallpaper';
import { el, button } from './dom';

function paint(root: HTMLElement, close: () => void): void {
  const s = getState();
  const wrap = el('div', { className: 'display-props' });

  const previewShell = el('div', { className: 'display-monitor' });
  const preview = el('div', { className: 'display-preview' });
  previewShell.append(preview);
  applyDesktopBackground(preview, s);

  const list = el('select', { className: 'display-list' });
  list.setAttribute('size', '7');
  list.setAttribute('aria-label', 'Wallpaper');
  for (const w of allWallpapers()) {
    const opt = document.createElement('option');
    opt.value = w.id;
    opt.textContent = w.title;
    if (w.id === s.wallpaperId) opt.selected = true;
    list.append(opt);
  }
  list.addEventListener('change', () => dispatch({ type: 'SET_WALLPAPER', id: list.value }));

  const fit = el('select', { className: 'display-fit' });
  fit.setAttribute('aria-label', 'Display');
  for (const f of FITS) {
    const opt = document.createElement('option');
    opt.value = f.id;
    opt.textContent = f.title;
    if (f.id === s.wallpaperFit) opt.selected = true;
    fit.append(opt);
  }
  fit.addEventListener('change', () => {
    const v = fit.value;
    if (v === 'center' || v === 'tile' || v === 'stretch' || v === 'fill') {
      dispatch({ type: 'SET_WALLPAPER_FIT', fit: v });
    }
  });

  const colors = el('div', { className: 'display-colors' });
  colors.setAttribute('role', 'group');
  colors.setAttribute('aria-label', 'Desktop color');
  for (const c of allColors()) {
    const sw = el('button', { className: 'color-swatch' });
    sw.type = 'button';
    sw.title = c.title;
    sw.setAttribute('aria-label', c.title);
    sw.style.background = c.hex;
    if (c.hex.toLowerCase() === s.desktopColor.toLowerCase()) sw.classList.add('is-selected');
    sw.addEventListener('click', () => dispatch({ type: 'SET_DESKTOP_COLOR', hex: c.hex }));
    colors.append(sw);
  }

  const skip = document.createElement('label');
  skip.className = 'w95-check';
  const box = document.createElement('input');
  box.type = 'checkbox';
  box.checked = s.skipBoot;
  box.addEventListener('change', () => dispatch({ type: 'SET_SKIP_BOOT', on: box.checked }));
  skip.append(box, document.createTextNode(' Skip “Starting Ben95” splash'));

  const fields = el('div', { className: 'display-fields' });
  fields.append(
    el('div', { className: 'display-label', text: 'Wallpaper' }),
    list,
    el('div', { className: 'display-label', text: 'Display' }),
    fit,
    el('div', { className: 'display-label', text: 'Desktop color' }),
    colors,
    skip,
  );

  const actions = el('div', { className: 'display-actions' });
  actions.append(button('OK', close));

  wrap.append(previewShell, fields, actions);
  root.replaceChildren(wrap);
}

export const displayApp: AppDef = {
  appId: 'display',
  title: 'Display Properties',
  icon: 'display',
  defaultSize: { w: 460, h: 420 },
  minSize: { w: 360, h: 320 },
  singleton: true,
  mount(root, ctx) {
    paint(root, ctx.close);
    const unsub = subscribe((state, event) => {
      if (
        event.type === 'SET_WALLPAPER' ||
        event.type === 'SET_WALLPAPER_FIT' ||
        event.type === 'SET_DESKTOP_COLOR' ||
        event.type === 'SET_SKIP_BOOT'
      ) {
        paint(root, ctx.close);
      }
      void state;
    });
    return () => {
      unsub();
      root.replaceChildren();
    };
  },
};
